import { Form, TextArea, useForm } from '@/components/form';
import { createModal } from '@/hooks/system/create-modal';
import { api } from '@/services/api/services-api';
import { sentryCaptureException } from '@/utils/sentry';
import { Button, Modal, ModalBody, ModalFooter, useSnackbars } from '@customerio/pluma-components/react';
import { z } from 'zod';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';

export const useFeedbackModal = createModal('feedback', {
  type: 'ephemeral',
});

interface FeedbackModalProps {
  feature: string;
  eventCategory?: string;
}

export function FeedbackModal({ feature, eventCategory = 'feedback' }: FeedbackModalProps) {
  const auth = useAuth();
  const feedbackModal = useFeedbackModal();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();

  const form = useForm({
    defaultValues: {
      feedback: '',
    },
    validators: {
      onChange: z.object({
        feedback: z.string().min(1, 'Feedback is required'),
      }),
    },
    async onSubmit(values) {
      try {
        await api.submitFeedback({
          params: { accountId: auth.account.id },
          body: {
            replyTo: auth.user.email,
            accountId: auth.account.id,
            userFirstName: auth.user.first_name,
            userLastName: auth.user.last_name,
            browser: navigator.userAgent,
            url: window.location.href,
            windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
            criticism: `Feedback from ${feature}`,
            praise: values.value.feedback,
          },
        });

        analytics.track(eventCategory, 'feedbackFormSubmitted', {
          Feedback: values.value.feedback,
          Feature: feature,
        });

        snackbars.create({
          message: 'Thank you for your feedback!',
          variant: 'success',
        });

        handleClose();
      } catch (error) {
        sentryCaptureException(error);
        snackbars.create({
          message: 'Failed to submit feedback. Please try again.',
          variant: 'error',
        });
      }
    },
  });

  const handleClose = () => {
    feedbackModal.close();
    form.reset();
  };

  return (
    <Modal isOpen={feedbackModal.isOpen} onClose={handleClose} title="Send us your feedback" size="md">
      <Form api={form}>
        <ModalBody>
          <TextArea
            api={form}
            name="feedback"
            label="What would you like to tell us?"
            placeholder="What can we improve?"
            minRows={4}
            maxRows={8}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Submit Feedback
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
